export const STORAGE_KEY = 'pnp.highScores.v1';

export const MAX_HIGH_SCORES = 5;
export const MAX_NAME_LENGTH = 8;

export interface PlayerIcon {
  readonly id: string;
  readonly emoji: string;
  readonly label: string;
}

export const PLAYER_ICONS = [
  { id: 'cat', emoji: '🐱', label: 'Cat' },
  { id: 'dog', emoji: '🐶', label: 'Dog' },
  { id: 'fox', emoji: '🦊', label: 'Fox' },
  { id: 'frog', emoji: '🐸', label: 'Frog' },
  { id: 'unicorn', emoji: '🦄', label: 'Unicorn' },
  { id: 'dragon', emoji: '🐲', label: 'Dragon' },
  { id: 'rocket', emoji: '🚀', label: 'Rocket' },
  { id: 'star', emoji: '⭐', label: 'Star' },
  { id: 'rainbow', emoji: '🌈', label: 'Rainbow' },
  { id: 'butterfly', emoji: '🦋', label: 'Butterfly' },
  { id: 'icecream', emoji: '🍦', label: 'Ice Cream' },
  { id: 'crown', emoji: '👑', label: 'Crown' },
] as const satisfies ReadonlyArray<PlayerIcon>;

export type PlayerIconId = (typeof PLAYER_ICONS)[number]['id'];

const ICON_IDS: ReadonlySet<string> = new Set(PLAYER_ICONS.map((p) => p.id));

export interface HighScoreEntry {
  icon: PlayerIconId;
  name: string | null;
  score: number;
  hits: number;
  bestCombo: number;
  date: number;
}

export type NewHighScoreInput = Omit<HighScoreEntry, 'date'>;

const NAME_RE = /^[A-Z]{1,8}$/;

function validateEntry(raw: unknown): HighScoreEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.icon !== 'string' || !ICON_IDS.has(r.icon)) return null;
  if (typeof r.score !== 'number' || !Number.isFinite(r.score)) return null;
  if (typeof r.hits !== 'number' || !Number.isFinite(r.hits)) return null;
  if (typeof r.bestCombo !== 'number' || !Number.isFinite(r.bestCombo)) return null;
  if (typeof r.date !== 'number' || !Number.isFinite(r.date)) return null;
  let name: string | null = null;
  if (typeof r.name === 'string') {
    const trimmed = r.name.toUpperCase().slice(0, MAX_NAME_LENGTH);
    if (NAME_RE.test(trimmed)) name = trimmed;
  } else if (r.name !== null && r.name !== undefined) {
    return null;
  }
  return {
    icon: r.icon as PlayerIconId,
    name,
    score: r.score,
    hits: r.hits,
    bestCombo: r.bestCombo,
    date: r.date,
  };
}

function validate(raw: unknown): HighScoreEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: HighScoreEntry[] = [];
  for (const item of raw) {
    const entry = validateEntry(item);
    if (entry) out.push(entry);
  }
  return out;
}

function read(): HighScoreEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return sortAndCap(validate(parsed));
  } catch {
    return [];
  }
}

function write(entries: HighScoreEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage full or unavailable — list simply won't persist this session
  }
}

function sortAndCap(entries: HighScoreEntry[]): HighScoreEntry[] {
  return [...entries].sort((a, b) => b.score - a.score).slice(0, MAX_HIGH_SCORES);
}

export function getHighScores(): HighScoreEntry[] {
  return read();
}

export function getTopScore(): number {
  return read()[0]?.score ?? 0;
}

export function isNewHighScore(score: number): boolean {
  if (score <= 0) return false;
  const list = read();
  if (list.length < MAX_HIGH_SCORES) return true;
  const last = list[list.length - 1];
  return last !== undefined && score > last.score;
}

export function addHighScore(input: NewHighScoreInput): HighScoreEntry {
  const entry: HighScoreEntry = { ...input, date: Date.now() };
  const next = sortAndCap([...read(), entry]);
  write(next);
  return entry;
}

export function resetHighScores(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
